import { aboutFacts, type About } from "../game/about.ts";
import type { GeoData } from "../game/geo.ts";
import { results, status } from "../game/rules.ts";
import { challengeUrl, shareText, sheetHeading, summaryLine, wasClose } from "../game/text.ts";
import type { PageData } from "../page.ts";
import { appLinks } from "./app-links.ts";
import { BAND_COLOURS } from "./play.ts";
import { h } from "./dom.ts";

export function resultAnnouncement(name: string, won: boolean, count: number, close = false): string {
  if (won) return `It was ${name}. You found it in ${count}.`;
  return close ? `It was ${name}. You were close!` : `It was ${name}. New photo tomorrow.`;
}

export const wikipediaUrl = (name: string): string => `https://en.wikipedia.org/wiki/${encodeURIComponent(name.replaceAll(" ", "_"))}`;

export interface ShareEnv {
  share?: ((data: { text: string }) => Promise<void>) | undefined;
  writeText?: ((text: string) => Promise<void>) | undefined;
}

/**
 * Shares with the system sheet if there is one, else copies. Only says "copied" after a real write.
 * "cancelled": the player closed the share sheet. "failed": nothing worked, so show the text to copy by hand.
 */
export async function shareOrCopy(text: string, env: ShareEnv): Promise<"shared" | "copied" | "cancelled" | "failed"> {
  if (env.share) {
    try { await env.share({ text }); return "shared"; }
    catch (e) { if (e instanceof Error && e.name === "AbortError") return "cancelled"; }
  }
  if (env.writeText) {
    try { await env.writeText(text); return "copied"; } catch { return "failed"; }
  }
  return "failed";
}

function browserEnv(): ShareEnv {
  return {
    share: typeof navigator.share === "function" ? (d) => navigator.share(d) : undefined,
    writeText: navigator.clipboard && typeof navigator.clipboard.writeText === "function" ? (t) => navigator.clipboard.writeText(t) : undefined,
  };
}

const restoreTimers = new WeakMap<HTMLButtonElement, ReturnType<typeof setTimeout>>();

async function share(text: string, button: HTMLButtonElement, label: string, live: HTMLElement, fallback: HTMLElement): Promise<void> {
  const outcome = await shareOrCopy(text, browserEnv());
  if (outcome === "failed") { showCopyField(fallback, text); return; }
  if (outcome !== "copied") return;
  button.textContent = "Copied";
  live.textContent = "";
  setTimeout(() => { live.textContent = "Copied"; }, 0);
  clearTimeout(restoreTimers.get(button));
  restoreTimers.set(button, setTimeout(() => { button.textContent = label; live.textContent = ""; }, 2000));
}

/** No share sheet and no clipboard: the text in a read-only field, selected, to copy by hand. */
function showCopyField(host: HTMLElement, text: string): void {
  const field = h("textarea", { id: "share-copy", class: "copy-field", readonly: true, rows: "3" });
  field.value = text;
  host.replaceChildren(h("label", { for: "share-copy", class: "small soft" }, "Copy this to share"), field);
  host.hidden = false;
  field.focus();
  field.select();
}

export function showResult(root: HTMLElement, ctx: { data: PageData; geo: GeoData; guesses: string[]; reducedMotion: boolean; fresh: boolean; about: Record<string, About> }): void {
  const { data, geo, guesses } = ctx;
  const about = ctx.about[data.puzzle.answer];
  const answer = geo.country(data.puzzle.answer)!;
  const st = status(answer.id, guesses);
  const won = st === "won";
  const rs = results(answer.id, guesses, geo);
  const close = wasClose(rs);

  const live = h("p", { class: "visually-hidden", role: "status" });
  const fallback = h("div", { class: "copy", hidden: true });
  const shareButton = h("button", { class: "secondary", type: "button" }, "Share my score");
  shareButton.addEventListener("click", () => void share(shareText(data.puzzle.number, rs, st, data.site), shareButton, "Share my score", live, fallback));
  const backButton = h("button", { class: "secondary", type: "button" }, "Challenge someone back");
  backButton.addEventListener("click", () => void share(challengeUrl(data.site, data.puzzle.number, guesses.length), backButton, "Challenge someone back", live, fallback));

  const getApp = appLinks(data, won ? "Get the app" : "Get tomorrow's photo in the app", "link underline");

  // Straight from play, the result rises in over the globe while the globe fades away underneath (the app's handoff).
  const leaving = ctx.fresh && !ctx.reducedMotion ? root.querySelector<HTMLElement>(".page.play") : null;
  root.replaceChildren(h("div", { class: `page result${leaving ? " arriving" : ""}` },
    h("header", { class: "topbar" }, h("p", { class: "mono muted" }, "WHERE IN THE WORLD?")),
    h("img", { class: "result-photo", src: data.puzzle.image.url, alt: data.puzzle.alt }),
    h("p", { class: "pin-line" }, answer.region),
    h("div", { class: "summary" },
      h("p", { class: "mono accent" }, summaryLine(st, guesses.length, data.challenge, close)),
      h("span", { class: "squares", "aria-hidden": "true" }, ...rs.map((r) => h("span", { class: "swatch", style: `background:${BAND_COLOURS[r.band]}` })))),
    h("p", { id: "result-announce", hidden: true }, resultAnnouncement(answer.name, won, guesses.length, close)),
    h("h1", { class: "answer", tabindex: "-1", "aria-describedby": "result-announce" }, answer.name),
    h("div", { class: "actions" }, shareButton, won ? backButton : null, live),
    fallback,
    h("section", { class: "about" },
      h("h2", { class: "mono muted about-title", "aria-label": `About ${answer.name}` }, `About ${answer.name}`.toUpperCase()),
      about ? h("dl", { class: "facts" }, ...aboutFacts(about).map(([label, value]) =>
        h("div", {}, h("dt", { class: "mono muted" }, label.toUpperCase()), h("dd", {}, value)))) : null,
      h("p", {}, h("a", { href: wikipediaUrl(answer.name), class: "underline", rel: "noopener" }, "Read more on Wikipedia ↗")),
      h("a", { class: "faint small credit", href: data.puzzle.credit.sourceUrl, rel: "noopener" },
        `Photo: ${data.puzzle.credit.author}, ${data.puzzle.credit.licence}. View the original`)),
    getApp));
  if (leaving) {
    leaving.inert = true;
    leaving.classList.add("leaving");
    root.prepend(leaving);
    setTimeout(() => leaving.remove(), 700);
  }
  (root.querySelector(".answer") as HTMLElement).focus();

  if (won && ctx.fresh) setTimeout(() => showSheet(root, ctx, guesses.length), 1000);
}

function showSheet(root: HTMLElement, ctx: { data: PageData; reducedMotion: boolean }, count: number): void {
  const [first, second] = sheetHeading(count, ctx.data.challenge);
  const sheet = h("dialog", { class: `sheet${ctx.reducedMotion ? "" : " slide"}`, "aria-labelledby": "sheet-title" },
    h("p", { class: "mono accent" }, "FREE ON IPHONE AND ANDROID"),
    h("h2", { id: "sheet-title", class: "serif", tabindex: "-1" }, first!, second ? h("br") : null, second ?? null),
    appLinks(ctx.data, "Get the free app", "primary"),
    h("form", { method: "dialog" }, h("button", { class: "link center-button" }, "Not now")));
  root.append(sheet);
  sheet.showModal();
  sheet.querySelector<HTMLElement>("#sheet-title")?.focus();
}
